import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';
import { subCommitmentsApi, projectsApi, subcontractorsApi, assignmentsApi } from '../api';
import { dollarsToCents, centsToDollarString, centsToDollarInput, fmtDate } from '../constants';
import { MoneyInput } from './MoneyInput';
import { COMMITMENT_TYPE_OPTIONS, COMMITMENT_STATUS_STYLES, commitmentTypeLabel, commitmentStatusLabel } from '../subConstants';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls = 'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';

const Row = ({ l, v }) => (
  <div className="flex justify-between gap-3 py-1 text-sm">
    <span className="text-gray-500">{l}</span>
    <span className="text-gray-200 text-right">{v}</span>
  </div>
);

export const CommitmentFormDialog = ({ open, onOpenChange, commitment, lockedProjectId, lockedSubcontractorId, onSaved }) => {
  const isNew = !commitment;
  const status = commitment?.status;
  const editable = isNew || status === 'draft';
  const [projects, setProjects] = useState([]);
  const [subs, setSubs] = useState([]);
  const [parents, setParents] = useState([]);
  const [onTeam, setOnTeam] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelMode, setCancelMode] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [f, setF] = useState({ project_id: '', subcontractor_id: '', commitment_type: 'original_scope', parent_commitment_id: '', title: '', description: '', work_category: '', commitment_date: '', amount: '', internal_notes: '' });

  useEffect(() => {
    if (!open) return;
    setCancelMode(false);
    setCancelReason('');
    if (editable) {
      projectsApi.list({ archived: false, sort: 'name_az' }).then((r) => setProjects(r.data)).catch(() => {});
      subcontractorsApi.list({ archived: false, status: 'active' }).then((r) => setSubs(r.data)).catch(() => {});
    }
    if (commitment) {
      setF({
        project_id: commitment.project_id, subcontractor_id: commitment.subcontractor_id,
        commitment_type: commitment.commitment_type, parent_commitment_id: commitment.parent_commitment_id || '',
        title: commitment.title || '', description: commitment.description || '',
        work_category: commitment.work_category || '', commitment_date: commitment.commitment_date,
        amount: centsToDollarInput(commitment.amount_cents), internal_notes: commitment.internal_notes || '',
      });
    } else {
      setF({ project_id: lockedProjectId || '', subcontractor_id: lockedSubcontractorId || '', commitment_type: 'original_scope', parent_commitment_id: '', title: '', description: '', work_category: '', commitment_date: new Date().toISOString().slice(0, 10), amount: '', internal_notes: '' });
    }
  }, [open, commitment, editable, lockedProjectId, lockedSubcontractorId]);

  // Parent options: same project + subcontractor, Original Scope only
  useEffect(() => {
    if (!open || !editable || f.commitment_type !== 'extra_work' || !f.project_id || !f.subcontractor_id) { setParents([]); return; }
    subCommitmentsApi.list({ project_id: f.project_id, subcontractor_id: f.subcontractor_id, commitment_type: 'original_scope' })
      .then((r) => setParents(r.data.filter((c) => c.id !== commitment?.id)))
      .catch(() => {});
  }, [open, editable, f.commitment_type, f.project_id, f.subcontractor_id, commitment?.id]);

  // Non-blocking Project Team warning
  useEffect(() => {
    if (!open || !editable || !f.project_id || !f.subcontractor_id) { setOnTeam(true); return; }
    assignmentsApi.list({ target_type: 'project', target_id: f.project_id, assignee_type: 'subcontractor', assignee_id: f.subcontractor_id, archived: false })
      .then((r) => setOnTeam(r.data.length > 0))
      .catch(() => setOnTeam(true));
  }, [open, editable, f.project_id, f.subcontractor_id]);

  const set = (k, v) => setF((x) => ({
    ...x, [k]: v,
    ...(k === 'commitment_type' && v === 'original_scope' ? { parent_commitment_id: '' } : {}),
    ...((k === 'project_id' || k === 'subcontractor_id') ? { parent_commitment_id: '' } : {}),
  }));

  const payload = () => ({
    project_id: f.project_id, subcontractor_id: f.subcontractor_id,
    commitment_type: f.commitment_type,
    parent_commitment_id: f.commitment_type === 'extra_work' ? (f.parent_commitment_id || null) : null,
    title: f.title, description: f.description || null, work_category: f.work_category || null,
    commitment_date: f.commitment_date, amount_cents: dollarsToCents(f.amount),
    internal_notes: f.internal_notes || null,
  });

  const validate = () => {
    if (!f.project_id) { toast.error('Select a project'); return false; }
    if (!f.subcontractor_id) { toast.error('Select a subcontractor'); return false; }
    if (!f.title.trim()) { toast.error('Title is required'); return false; }
    if (!f.commitment_date) { toast.error('Commitment date is required'); return false; }
    const cents = dollarsToCents(f.amount);
    if (cents == null || cents <= 0) { toast.error('Amount must be greater than $0'); return false; }
    return true;
  };

  const save = async (approve = false) => {
    if (busy || !validate()) return;
    setBusy(true);
    try {
      const res = isNew ? await subCommitmentsApi.create(payload()) : await subCommitmentsApi.update(commitment.id, payload());
      let doc = res.data;
      if (approve) doc = (await subCommitmentsApi.approve(doc.id)).data;
      toast.success(approve ? 'Commitment approved' : 'Commitment saved as draft');
      onOpenChange(false);
      onSaved?.(doc);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save commitment');
    } finally { setBusy(false); }
  };

  const action = async (fn, msg) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Action failed');
    } finally { setBusy(false); }
  };

  const doCancel = () => {
    if (status && status !== 'draft' && !cancelReason.trim()) {
      toast.error('A cancel reason is required for approved or completed commitments');
      return;
    }
    action(() => subCommitmentsApi.cancel(commitment.id, cancelReason.trim() || null), 'Commitment cancelled');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="commitment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isNew ? 'New Subcontractor Commitment' : 'Commitment'}
            {status && <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${COMMITMENT_STATUS_STYLES[status]}`}>{commitmentStatusLabel(status)}</span>}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            {editable
              ? 'Draft commitments do not count as project cost until approved.'
              : status === 'cancelled'
                ? 'Cancelled — no longer counts as project cost. Restore to draft to use again.'
                : 'Counts as project subcontractor cost. Reopen to draft to edit financial fields.'}
          </DialogDescription>
        </DialogHeader>

        {editable ? (
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Project *</label>
                <select value={f.project_id} onChange={(e) => set('project_id', e.target.value)} className={selectCls} disabled={Boolean(lockedProjectId)} data-testid="cm-project">
                  <option value="">Select project…</option>
                  {lockedProjectId && !projects.some((p) => p.id === lockedProjectId) && <option value={lockedProjectId}>Current Project</option>}
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Subcontractor *</label>
                <select value={f.subcontractor_id} onChange={(e) => set('subcontractor_id', e.target.value)} className={selectCls} disabled={Boolean(lockedSubcontractorId)} data-testid="cm-subcontractor">
                  <option value="">Select subcontractor…</option>
                  {lockedSubcontractorId && !subs.some((s) => s.id === lockedSubcontractorId) && <option value={lockedSubcontractorId}>Current Subcontractor</option>}
                  {subs.map((s) => <option key={s.id} value={s.id}>{s.display_name}</option>)}
                </select>
              </div>
            </div>
            {!onTeam && f.project_id && f.subcontractor_id && (
              <p className="flex items-center gap-2 text-xs text-amber-400/90" data-testid="cm-team-warning">
                <AlertTriangle size={13} className="shrink-0" /> This subcontractor is not currently on the Project Team.
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Commitment Type *</label>
                <select value={f.commitment_type} onChange={(e) => set('commitment_type', e.target.value)} className={selectCls} data-testid="cm-type">
                  {COMMITMENT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {f.commitment_type === 'extra_work' && (
                <div>
                  <label className="text-sm text-gray-300 mb-1.5 block font-medium">Parent Commitment</label>
                  <select value={f.parent_commitment_id} onChange={(e) => set('parent_commitment_id', e.target.value)} className={selectCls} data-testid="cm-parent">
                    <option value="">No parent (standalone extra)</option>
                    {parents.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">Title *</label>
              <Input value={f.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g., Electrical Package" className={inputCls} data-testid="cm-title" />
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">Description</label>
              <Input value={f.description} onChange={(e) => set('description', e.target.value)} className={inputCls} data-testid="cm-description" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Work Category</label>
                <Input value={f.work_category} onChange={(e) => set('work_category', e.target.value)} placeholder="e.g., Electrical" className={inputCls} data-testid="cm-category" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Date *</label>
                <Input type="date" value={f.commitment_date} onChange={(e) => set('commitment_date', e.target.value)} className={inputCls} data-testid="cm-date" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Amount ($) *</label>
                <MoneyInput value={f.amount} onChange={(v) => set('amount', v)} className={inputCls} data-testid="cm-amount" />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">Internal Notes</label>
              <Input value={f.internal_notes} onChange={(e) => set('internal_notes', e.target.value)} className={inputCls} data-testid="cm-notes" />
            </div>
          </div>
        ) : (
          <div className="space-y-1 py-1" data-testid="cm-readonly">
            <Row l="Project" v={commitment.project?.name} />
            <Row l="Subcontractor" v={commitment.subcontractor?.display_name} />
            <Row l="Type" v={commitmentTypeLabel(commitment.commitment_type)} />
            {commitment.parent_commitment && <Row l="Parent Commitment" v={commitment.parent_commitment.title} />}
            <Row l="Title" v={commitment.title} />
            {commitment.description && <Row l="Description" v={commitment.description} />}
            {commitment.work_category && <Row l="Category" v={commitment.work_category} />}
            <Row l="Date" v={fmtDate(commitment.commitment_date)} />
            <Row l="Amount" v={centsToDollarString(commitment.amount_cents)} />
            {commitment.internal_notes && <Row l="Internal Notes" v={commitment.internal_notes} />}
            <div className="border-t border-gray-800 my-2" />
            {commitment.approved_at && <Row l="Approved" v={`${fmtDate(commitment.approved_at.slice(0, 10))} by ${commitment.approved_by_username}`} />}
            {commitment.completed_at && <Row l="Completed" v={`${fmtDate(commitment.completed_at.slice(0, 10))} by ${commitment.completed_by_username}`} />}
            {commitment.cancelled_at && <Row l="Cancelled" v={`${fmtDate(commitment.cancelled_at.slice(0, 10))} by ${commitment.cancelled_by_username}`} />}
            {commitment.cancel_reason && <Row l="Cancel Reason" v={commitment.cancel_reason} />}
            <Row l="Created" v={`${fmtDate(commitment.created_at?.slice(0, 10))} by ${commitment.created_by_username}`} />
          </div>
        )}

        {cancelMode && (
          <div className="bg-red-950/30 border border-red-900 rounded-md p-3 space-y-2" data-testid="cm-cancel-panel">
            <p className="text-sm text-red-300">
              Cancel this commitment? It will stop counting as project cost immediately.
              {' '}If payments were already recorded for this project/subcontractor, cancelling may create an advance or overpayment balance.
            </p>
            <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder={status === 'draft' ? 'Cancel reason (optional)' : 'Cancel reason (required)'} className={inputCls} data-testid="cm-cancel-reason" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCancelMode(false)} className="border-gray-700 text-gray-300" size="sm">Keep Commitment</Button>
              <Button onClick={doCancel} disabled={busy} className="bg-red-600 text-white hover:bg-red-700" size="sm" data-testid="cm-cancel-confirm">Confirm Cancel</Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-gray-800">
          {editable && (
            <>
              {!isNew && (
                <Button variant="outline" onClick={() => setCancelMode(true)} disabled={busy || cancelMode} className="border-red-900 text-red-400 mr-auto" data-testid="cm-cancel-btn">Cancel Commitment</Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-gray-700 text-gray-300">Close</Button>
              <Button variant="outline" onClick={() => save(false)} disabled={busy} className="border-gray-700 text-gray-300" data-testid="cm-save-draft">
                {busy && <Loader2 size={14} className="animate-spin" />} Save Draft
              </Button>
              <Button onClick={() => save(true)} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="cm-save-approve">
                {busy && <Loader2 size={14} className="animate-spin" />} Save &amp; Approve
              </Button>
            </>
          )}
          {status === 'approved' && (
            <>
              <Button variant="outline" onClick={() => setCancelMode(true)} disabled={busy || cancelMode} className="border-red-900 text-red-400 mr-auto" data-testid="cm-cancel-btn">Cancel Commitment</Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300">Close</Button>
              <Button variant="outline" onClick={() => action(() => subCommitmentsApi.reopen(commitment.id), 'Reopened to draft')} disabled={busy} className="border-gray-700 text-gray-300" data-testid="cm-reopen">Reopen to Draft</Button>
              <Button onClick={() => action(() => subCommitmentsApi.complete(commitment.id), 'Commitment completed')} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="cm-complete">Mark Completed</Button>
            </>
          )}
          {status === 'completed' && (
            <>
              <Button variant="outline" onClick={() => setCancelMode(true)} disabled={busy || cancelMode} className="border-red-900 text-red-400 mr-auto" data-testid="cm-cancel-btn">Cancel Commitment</Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300">Close</Button>
              <Button onClick={() => action(() => subCommitmentsApi.reopen(commitment.id), 'Reopened to draft')} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="cm-reopen">Reopen to Draft</Button>
            </>
          )}
          {status === 'cancelled' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300">Close</Button>
              <Button onClick={() => action(() => subCommitmentsApi.restore(commitment.id), 'Restored to draft')} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="cm-restore">Restore to Draft</Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
